import { Badge, Box, Paper, Stack, Typography } from "@mui/material";

import AppBadge from "@/common/components/Badge/AppBadge";
import AppButton from "@/common/components/Button/AppButton";
import AppIconButton from "@/common/components/Button/AppIconButton";
import InlineCode from "@/common/components/Code/InlineCode";
import AppIcon from "@/common/components/Icons/AppIcon";
import AppTooltip from "@/common/components/Tooltip/AppTooltip";

export function DevBadges() {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography component='div' variant='h4' sx={{ my: 2 }}>
        Badges
      </Typography>

      <Stack gap={2}>
        <Typography component='div' variant='h5' sx={{ my: 2 }}>
          <InlineCode>MUI Badge</InlineCode>
        </Typography>

        <Stack gap={2}>
          <Typography variant='subtitle1'>variant</Typography>

          <Stack direction='row' gap={2}>
            <Badge variant='standard' overlap='rectangular' badgeContent={9}>
              <AppButton variant='outlined' size='small' color='text'>
                standard
              </AppButton>
            </Badge>
            <Badge variant='dot' overlap='rectangular' badgeContent={9}>
              <AppButton variant='outlined' size='small' color='text'>
                dot
              </AppButton>
            </Badge>
          </Stack>

          <Stack direction='row' gap={2}>
            <AppTooltip title='standard (of AppIconButton)' isDefaultEnterDelays>
              <Badge variant='standard' overlap='rectangular' badgeContent={9}>
                <AppIconButton>
                  <AppIcon of='data' />
                </AppIconButton>
              </Badge>
            </AppTooltip>
            <AppTooltip title='dot (of AppIconButton)' isDefaultEnterDelays>
              <Badge variant='dot' overlap='rectangular' badgeContent={9}>
                <AppIconButton>
                  <AppIcon of='data' />
                </AppIconButton>
              </Badge>
            </AppTooltip>
          </Stack>

          <Stack direction='row' gap={2}>
            <AppTooltip title='standard (of AppIcon)' isDefaultEnterDelays>
              <Badge variant='standard' overlap='rectangular' badgeContent={9}>
                <AppIcon of='data' fontSize='medium' />
              </Badge>
            </AppTooltip>
            <AppTooltip title='dot (of AppIcon)' isDefaultEnterDelays>
              <Badge variant='dot' overlap='rectangular' badgeContent={9}>
                <AppIcon of='data' fontSize='medium' />
              </Badge>
            </AppTooltip>
          </Stack>
        </Stack>

        <Stack gap={2}>
          <Typography variant='subtitle1'>overlap</Typography>

          <Stack direction='row' gap={2}>
            <Badge variant='standard' overlap='rectangular' badgeContent={9}>
              <AppButton variant='outlined' size='small' color='text'>
                standard overlap=rectangular
              </AppButton>
            </Badge>
            <Badge variant='dot' overlap='rectangular' badgeContent={9}>
              <AppButton variant='outlined' size='small' color='text'>
                dot overlap=rectangular
              </AppButton>
            </Badge>
          </Stack>

          <Stack direction='row' gap={1}>
            <AppTooltip title='variant=standard, overlap=rectangular' isDefaultEnterDelays>
              <Badge variant='standard' overlap='rectangular' badgeContent={9}>
                <AppIconButton>
                  <AppIcon of='data' />
                </AppIconButton>
              </Badge>
            </AppTooltip>
            <AppTooltip title='variant=dot, overlap=rectangular' isDefaultEnterDelays>
              <Badge variant='dot' overlap='rectangular' badgeContent={9}>
                <AppIconButton>
                  <AppIcon of='data' />
                </AppIconButton>
              </Badge>
            </AppTooltip>
            <AppTooltip title='variant=standard, overlap=circular' isDefaultEnterDelays>
              <Badge variant='standard' overlap='circular' badgeContent={9}>
                <AppIconButton>
                  <AppIcon of='data' />
                </AppIconButton>
              </Badge>
            </AppTooltip>
            <AppTooltip title='variant=dot, overlap=circular' isDefaultEnterDelays>
              <Badge variant='dot' overlap='circular' badgeContent={9}>
                <AppIconButton>
                  <AppIcon of='data' />
                </AppIconButton>
              </Badge>
            </AppTooltip>
          </Stack>
        </Stack>

        <Stack gap={2}>
          <Typography variant='subtitle1'>color</Typography>

          <Stack direction='row' spacing={2}>
            <Badge variant='standard' color='default' badgeContent={9}>
              <Box component='span'>standard default</Box>
            </Badge>

            <Badge variant='standard' color='primary' badgeContent={9}>
              <Box component='span'>standard primary</Box>
            </Badge>

            <Badge variant='standard' color='secondary' badgeContent={9}>
              <Box component='span'>standard secondary</Box>
            </Badge>

            <Badge variant='standard' color='info' badgeContent={9}>
              <Box component='span'>standard info</Box>
            </Badge>

            <Badge variant='standard' color='error' badgeContent={9}>
              <Box component='span'>standard error</Box>
            </Badge>

            <Badge variant='standard' color='warning' badgeContent={9}>
              <Box component='span'>standard warning</Box>
            </Badge>

            <Badge variant='standard' color='success' badgeContent={9}>
              <Box component='span'>standard success</Box>
            </Badge>
          </Stack>

          <Stack direction='row' spacing={2}>
            <Badge variant='dot' color='default' badgeContent={9}>
              <Box component='span'>dot default</Box>
            </Badge>

            <Badge variant='dot' color='primary' badgeContent={9}>
              <Box component='span'>dot primary</Box>
            </Badge>

            <Badge variant='dot' color='secondary' badgeContent={9}>
              <Box component='span'>dot secondary</Box>
            </Badge>

            <Badge variant='dot' color='info' badgeContent={9}>
              <Box component='span'>dot info</Box>
            </Badge>

            <Badge variant='dot' color='error' badgeContent={9}>
              <Box component='span'>dot error</Box>
            </Badge>

            <Badge variant='dot' color='warning' badgeContent={9}>
              <Box component='span'>dot warning</Box>
            </Badge>

            <Badge variant='dot' color='success' badgeContent={9}>
              <Box component='span'>dot success</Box>
            </Badge>
          </Stack>
        </Stack>

        <Stack gap={2}>
          <Typography variant='subtitle1'>anchorOrigin</Typography>

          <Stack direction='row' spacing={4}>
            <Badge
              variant='standard'
              color='primary'
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              badgeContent={9}
            >
              <Box component='span'>top left</Box>
            </Badge>

            <Badge
              variant='standard'
              color='primary'
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              badgeContent={9}
            >
              <Box component='span'>top right</Box>
            </Badge>

            <Badge
              variant='standard'
              color='primary'
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              badgeContent={9}
            >
              <Box component='span'>bottom left</Box>
            </Badge>

            <Badge
              variant='standard'
              color='primary'
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent={9}
            >
              <Box component='span'>bottom right</Box>
            </Badge>
          </Stack>
        </Stack>
      </Stack>

      <Typography component='div' variant='h5' sx={{ my: 2 }}>
        <InlineCode>AppBadge</InlineCode>
      </Typography>

      <Stack gap={2}>
        <Typography variant='subtitle1'>color</Typography>

        <Stack direction='row' spacing={2}>
          <AppBadge variant='standard' color='default' badgeContent={9}>
            <Box component='span'>standard default</Box>
          </AppBadge>

          <AppBadge variant='standard' color='text' badgeContent={9}>
            <Box component='span'>standard text</Box>
          </AppBadge>

          <AppBadge variant='standard' color='primary' badgeContent={9}>
            <Box component='span'>standard primary</Box>
          </AppBadge>

          <AppBadge variant='standard' color='secondary' badgeContent={9}>
            <Box component='span'>standard secondary</Box>
          </AppBadge>

          <AppBadge variant='standard' color='info' badgeContent={9}>
            <Box component='span'>standard info</Box>
          </AppBadge>

          <AppBadge variant='standard' color='error' badgeContent={9}>
            <Box component='span'>standard error</Box>
          </AppBadge>

          <AppBadge variant='standard' color='warning' badgeContent={9}>
            <Box component='span'>standard warning</Box>
          </AppBadge>

          <AppBadge variant='standard' color='success' badgeContent={9}>
            <Box component='span'>standard success</Box>
          </AppBadge>
        </Stack>

        <Stack direction='row' spacing={2}>
          <AppBadge variant='dot' color='default' badgeContent={9}>
            <Box component='span'>dot default</Box>
          </AppBadge>

          <AppBadge variant='dot' color='text' badgeContent={9}>
            <Box component='span'>dot text</Box>
          </AppBadge>

          <AppBadge variant='dot' color='primary' badgeContent={9}>
            <Box component='span'>dot primary</Box>
          </AppBadge>

          <AppBadge variant='dot' color='secondary' badgeContent={9}>
            <Box component='span'>dot secondary</Box>
          </AppBadge>

          <AppBadge variant='dot' color='info' badgeContent={9}>
            <Box component='span'>dot info</Box>
          </AppBadge>

          <AppBadge variant='dot' color='error' badgeContent={9}>
            <Box component='span'>dot error</Box>
          </AppBadge>

          <AppBadge variant='dot' color='warning' badgeContent={9}>
            <Box component='span'>dot warning</Box>
          </AppBadge>

          <AppBadge variant='dot' color='success' badgeContent={9}>
            <Box component='span'>dot success</Box>
          </AppBadge>
        </Stack>
      </Stack>

      <Stack gap={2}>
        <Typography variant='subtitle1'>size</Typography>

        <Stack direction='row' spacing={2}>
          <AppBadge variant='standard' color='primary' size='small' badgeContent={9}>
            <Box component='span'>standard small</Box>
          </AppBadge>

          <AppBadge variant='standard' color='primary' size='small' badgeContent={99}>
            <Box component='span'>standard small</Box>
          </AppBadge>

          <AppBadge variant='standard' color='primary' size='small' badgeContent={999}>
            <Box component='span'>standard small</Box>
          </AppBadge>
        </Stack>

        <Stack direction='row' spacing={2}>
          <AppBadge variant='standard' color='primary' size='small' badgeContent={9}>
            <AppIcon of='data' fontSize='medium' />
          </AppBadge>
          <AppBadge variant='standard' color='primary' size='small' badgeContent={99}>
            <AppIcon of='data' fontSize='medium' />
          </AppBadge>
          <AppBadge variant='standard' color='primary' size='small' badgeContent={999}>
            <AppIcon of='data' fontSize='medium' />
          </AppBadge>
        </Stack>

        <Stack direction='row' spacing={2}>
          <AppBadge variant='standard' color='primary' size='small' badgeContent={9}>
            <AppTooltip title='variant=standard, size=small' isDefaultEnterDelays>
              <AppIcon of='data' fontSize='medium' />
            </AppTooltip>
          </AppBadge>
          <AppBadge variant='standard' color='primary' size='small' badgeContent={99}>
            <AppTooltip title='variant=standard, size=small' isDefaultEnterDelays>
              <AppIcon of='data' fontSize='medium' />
            </AppTooltip>
          </AppBadge>
          <AppBadge variant='standard' color='primary' size='small' badgeContent={999}>
            <AppTooltip title='variant=standard, size=small' isDefaultEnterDelays>
              <AppIcon of='data' fontSize='medium' />
            </AppTooltip>
          </AppBadge>
        </Stack>

        <Stack direction='row' spacing={2}>
          <AppBadge variant='standard' color='primary' size='medium' badgeContent={9}>
            <AppTooltip title='variant=standard, size=medium' isDefaultEnterDelays>
              <AppIcon of='data' fontSize='medium' />
            </AppTooltip>
          </AppBadge>

          <AppBadge variant='standard' color='primary' size='medium' badgeContent={99}>
            <AppTooltip title='variant=standard, size=medium' isDefaultEnterDelays>
              <AppIcon of='data' fontSize='medium' />
            </AppTooltip>
          </AppBadge>

          <AppBadge variant='standard' color='primary' size='medium' badgeContent={999}>
            <AppTooltip title='variant=standard, size=medium' isDefaultEnterDelays>
              <AppIcon of='data' fontSize='medium' />
            </AppTooltip>
          </AppBadge>
        </Stack>

        <Stack direction='row' spacing={2}>
          <AppBadge variant='dot' color='primary' size='small' badgeContent={9}>
            <Box component='span'>dot small</Box>
          </AppBadge>

          <AppBadge variant='dot' color='primary' size='medium' badgeContent={9}>
            <Box component='span'>dot medium</Box>
          </AppBadge>
        </Stack>

        <Stack direction='row' spacing={2}>
          <AppBadge variant='dot' color='primary' size='small' badgeContent={9}>
            <AppTooltip title='variant=dot, size=small' isDefaultEnterDelays>
              <AppIcon of='data' fontSize='medium' />
            </AppTooltip>
          </AppBadge>

          <AppBadge variant='dot' color='primary' size='medium' badgeContent={9}>
            <AppTooltip title='variant=dot, size=medium' isDefaultEnterDelays>
              <AppIcon of='data' fontSize='medium' />
            </AppTooltip>
          </AppBadge>
        </Stack>
      </Stack>

      <Typography component='div' variant='h6' sx={{ my: 2 }}>
        <InlineCode>overlap=rectangular</InlineCode>
      </Typography>
      <Stack sx={{ my: 4 }} direction='row' columnGap={4} rowGap={1} flexWrap='wrap'>
        <AppBadge
          variant='standard'
          color='primary'
          overlap='rectangular'
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          badgeContent={9}
        >
          <Box sx={{ p: 1, background: "beige" }} component='span'>
            top left
          </Box>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='rectangular'
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          badgeContent={9}
        >
          <Box sx={{ p: 1, background: "beige" }} component='span'>
            top center
          </Box>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='rectangular'
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          badgeContent={9}
        >
          <Box sx={{ p: 1, background: "beige" }} component='span'>
            top right
          </Box>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='rectangular'
          anchorOrigin={{ vertical: "center", horizontal: "left" }}
          badgeContent={9}
        >
          <Box sx={{ p: 1, background: "beige" }} component='span'>
            center left
          </Box>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='rectangular'
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          badgeContent={9}
        >
          <Box sx={{ p: 1, background: "beige" }} component='span'>
            center center
          </Box>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='rectangular'
          anchorOrigin={{ vertical: "center", horizontal: "right" }}
          badgeContent={9}
        >
          <Box sx={{ p: 1, background: "beige" }} component='span'>
            center right
          </Box>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='rectangular'
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          badgeContent={9}
        >
          <Box sx={{ p: 1, background: "beige" }} component='span'>
            bottom left
          </Box>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='rectangular'
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          badgeContent={9}
        >
          <Box sx={{ p: 1, background: "beige" }} component='span'>
            bottom center
          </Box>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='rectangular'
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          badgeContent={9}
        >
          <Box sx={{ p: 1, background: "beige" }} component='span'>
            bottom right
          </Box>
        </AppBadge>
      </Stack>
      <Stack sx={{ my: 4 }} direction='row' columnGap={4} rowGap={1} flexWrap='wrap'>
        <AppBadge
          variant='standard'
          color='primary'
          overlap='rectangular'
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          badgeContent={9}
        >
          <AppIconButton>
            <AppIcon of='data' />
          </AppIconButton>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='rectangular'
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          badgeContent={9}
        >
          <AppIconButton>
            <AppIcon of='data' />
          </AppIconButton>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='rectangular'
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          badgeContent={9}
        >
          <AppIconButton>
            <AppIcon of='data' />
          </AppIconButton>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='rectangular'
          anchorOrigin={{ vertical: "center", horizontal: "left" }}
          badgeContent={9}
        >
          <AppIconButton>
            <AppIcon of='data' />
          </AppIconButton>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='rectangular'
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          badgeContent={9}
        >
          <AppIconButton>
            <AppIcon of='data' />
          </AppIconButton>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='rectangular'
          anchorOrigin={{ vertical: "center", horizontal: "right" }}
          badgeContent={9}
        >
          <AppIconButton>
            <AppIcon of='data' />
          </AppIconButton>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='rectangular'
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          badgeContent={9}
        >
          <AppIconButton>
            <AppIcon of='data' />
          </AppIconButton>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='rectangular'
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          badgeContent={9}
        >
          <AppIconButton>
            <AppIcon of='data' />
          </AppIconButton>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='rectangular'
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          badgeContent={9}
        >
          <AppIconButton>
            <AppIcon of='data' />
          </AppIconButton>
        </AppBadge>
      </Stack>

      <Typography component='div' variant='h6' sx={{ my: 2 }}>
        <InlineCode>overlap=circular</InlineCode>
      </Typography>
      <Stack sx={{ my: 4 }} direction='row' columnGap={4} rowGap={1} flexWrap='wrap'>
        <AppBadge
          variant='standard'
          color='primary'
          overlap='circular'
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          badgeContent={9}
        >
          <Box sx={{ p: 1, background: "beige" }} component='span'>
            circular top left
          </Box>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='circular'
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          badgeContent={9}
        >
          <Box sx={{ p: 1, background: "beige" }} component='span'>
            circular top center
          </Box>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='circular'
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          badgeContent={9}
        >
          <Box sx={{ p: 1, background: "beige" }} component='span'>
            circular top right
          </Box>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='circular'
          anchorOrigin={{ vertical: "center", horizontal: "left" }}
          badgeContent={9}
        >
          <Box sx={{ p: 1, background: "beige" }} component='span'>
            circular center left
          </Box>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='circular'
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          badgeContent={9}
        >
          <Box sx={{ p: 1, background: "beige" }} component='span'>
            circular center center
          </Box>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='circular'
          anchorOrigin={{ vertical: "center", horizontal: "right" }}
          badgeContent={9}
        >
          <Box sx={{ p: 1, background: "beige" }} component='span'>
            circular center right
          </Box>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='circular'
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          badgeContent={9}
        >
          <Box sx={{ p: 1, background: "beige" }} component='span'>
            circular bottom left
          </Box>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='circular'
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          badgeContent={9}
        >
          <Box sx={{ p: 1, background: "beige" }} component='span'>
            circular bottom center
          </Box>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='circular'
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          badgeContent={9}
        >
          <Box sx={{ p: 1, background: "beige" }} component='span'>
            circular bottom right
          </Box>
        </AppBadge>
      </Stack>
      <Stack sx={{ my: 4 }} direction='row' columnGap={4} rowGap={1} flexWrap='wrap'>
        <AppBadge
          variant='standard'
          color='primary'
          overlap='circular'
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          badgeContent={9}
        >
          <AppIconButton>
            <AppIcon of='data' />
          </AppIconButton>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='circular'
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          badgeContent={9}
        >
          <AppIconButton>
            <AppIcon of='data' />
          </AppIconButton>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='circular'
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          badgeContent={9}
        >
          <AppIconButton>
            <AppIcon of='data' />
          </AppIconButton>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='circular'
          anchorOrigin={{ vertical: "center", horizontal: "left" }}
          badgeContent={9}
        >
          <AppIconButton>
            <AppIcon of='data' />
          </AppIconButton>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='circular'
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          badgeContent={9}
        >
          <AppIconButton>
            <AppIcon of='data' />
          </AppIconButton>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='circular'
          anchorOrigin={{ vertical: "center", horizontal: "right" }}
          badgeContent={9}
        >
          <AppIconButton>
            <AppIcon of='data' />
          </AppIconButton>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='circular'
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          badgeContent={9}
        >
          <AppIconButton>
            <AppIcon of='data' />
          </AppIconButton>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='circular'
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          badgeContent={9}
        >
          <AppIconButton>
            <AppIcon of='data' />
          </AppIconButton>
        </AppBadge>

        <AppBadge
          variant='standard'
          color='primary'
          overlap='circular'
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          badgeContent={9}
        >
          <AppIconButton>
            <AppIcon of='data' />
          </AppIconButton>
        </AppBadge>
      </Stack>
    </Paper>
  );
}
